/**
 * Format currency values to Brazilian Real (BRL)
 * 
 * @param valueInCentavos - The numeric value in centavos (e.g., 10000 = R$ 100,00)
 * @returns Formatted currency string (e.g., "R$ 1.234,56")
 */
export function formatCurrency(valueInCentavos: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valueInCentavos / 100);
}

/**
 * Format currency in a compact form for tight UI layouts (KPI cards, etc.).
 * Returns both a compact `display` string and the full BRL string for tooltips.
 *
 * Rules (input is centavos, divided by 100 to obtain reais):
 *  - reais < 10.000        → display = full (R$ 9.999,99)
 *  - 10.000 ≤ reais < 100k → "R$ X,Yk" (up to 1 decimal, pt-BR)
 *  - 100k   ≤ reais < 1M   → "R$ Xk"   (no decimals)
 *  - reais ≥ 1.000.000     → "R$ X,YM" (up to 1 decimal)
 *
 * @param valueInCentavos - Numeric value in centavos
 * @returns `{ display, full }` — `display` for the UI, `full` for tooltips
 */
export function formatCurrencyCompact(
  valueInCentavos: number,
): { display: string; full: string } {
  const full = formatCurrency(valueInCentavos);
  const reais = valueInCentavos / 100;
  const abs = Math.abs(reais);
  const sign = reais < 0 ? '-' : '';

  if (abs < 10_000) {
    return { display: full, full };
  }

  if (abs < 1_000_000) {
    const k = abs / 1000;
    const fractionDigits = abs < 100_000 ? 1 : 0;
    const formatted = k.toLocaleString('pt-BR', {
      maximumFractionDigits: fractionDigits,
      useGrouping: false,
    });
    // Prevent "1000k" when rounding pushes k to 1000; fall through to M branch instead.
    if (formatted !== '1000') {
      return { display: `${sign}R$ ${formatted}k`, full };
    }
  }

  const m = abs / 1_000_000;
  const formatted = m.toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
    useGrouping: false,
  });
  return { display: `${sign}R$ ${formatted}M`, full };
}

/**
 * Format a percentage already expressed in percent units (18.3 → "18,3%").
 * Valores redondos perdem a casa decimal (18 → "18%").
 */
export function formatPercentBR(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

/**
 * Format a duration given in SECONDS into a short human string.
 *
 * 0/negativo/não-finito → '—', < 60 → '{X}s', < 1h → '{X}min',
 * < 24h → '{X}h', >= 24h → '{X}d {Y}h' (o 'h' some quando é zero).
 *
 * @param seconds - Duração em segundos
 * @returns String curta ("35min", "2h", "1d 3h") ou '—' quando não há dado
 */
export function formatSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';

  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;

  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

/**
 * Format a duration given in DAYS ("5 dias", "1 dia").
 *
 * 0/negativo/não-finito → '—'; frações abaixo de meio dia → '< 1 dia'.
 *
 * @param days - Duração em dias (pode ser fracionária)
 */
export function formatDays(days: number): string {
  if (!Number.isFinite(days) || days <= 0) return '—';

  const rounded = Math.round(days);
  if (rounded < 1) return '< 1 dia';
  return `${rounded} ${rounded === 1 ? 'dia' : 'dias'}`;
}

/**
 * Format phone numbers to Brazilian format
 * 
 * @param phone - The phone number string (supports E.164 format like +5511999999999)
 * @returns Formatted phone string (e.g., "(11) 98765-4321")
 */
export function formatPhone(phone: string): string {
  if (!phone) return '';
  
  const cleaned = phone.replace(/\D/g, '');
  
  // Remove o código do país (55) se presente
  const digits = cleaned.startsWith('55') && cleaned.length > 11 
    ? cleaned.slice(2) 
    : cleaned;
  
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  
  return phone;
}

/**
 * Format date to Brazilian format
 *
 * Ausente/inválido → '—' (mesma convenção de `formatSeconds`, `formatDays` e
 * `formatTimeInStage`, e das cópias locais em `CouponsTable`/`DealsTable`/
 * `TenantChargesTable`). O helper NUNCA lança: `Intl.format()` recebendo
 * `Invalid Date` joga `RangeError` e, num render, derruba a árvore inteira no
 * ErrorBoundary — foi assim que um `createdAt` ausente no cabeçalho do modal de
 * contato matou o CRM inteiro (ATR-622).
 *
 * @param date - The date to format (aceita nulo: o campo pode faltar em runtime)
 * @returns Formatted date string (e.g., "31/12/2024") ou '—'
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR').format(parsed);
}

/**
 * Parse a date-only string into a Date, tolerating full ISO timestamps.
 *
 * `"YYYY-MM-DD"` → meia-noite LOCAL (preserva o fix de fuso da ATR-435 —
 * `new Date('2026-01-01')` puro viraria UTC e escorregaria um dia no Brasil).
 * ISO completo (ex.: `"2026-07-15T03:00:00.000Z"`) → parse direto.
 * Inválido → `null` — o consumidor escolhe o fallback, nunca "Invalid Date".
 *
 * @param value - Date string from the API
 * @returns Parsed Date or null when the value is not a valid date
 */
export function parseDateOnly(value: string): Date | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format datetime to Brazilian format with time
 *
 * @param date - The datetime to format
 * @returns Formatted datetime string (e.g., "31/12/2024 às 14:30")
 */
export function formatDateTime(date: string | Date): string {
  const formatter = new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  } as Intl.DateTimeFormatOptions);
  return formatter.format(new Date(date));
}

/**
 * Format date relative to now (e.g., "há 5 minutos", "ontem", "há 2 dias")
 * 
 * @param date - The date to format
 * @returns Relative date string
 */
export function formatRelativeDate(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();

  // Handle invalid dates or future timestamps (clock skew)
  if (isNaN(diffMs) || diffMs < 0) {
    return 'agora';
  }

  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'agora';
  }
  
  if (diffMins < 60) {
    return `há ${diffMins} min`;
  }
  
  if (diffHours < 24) {
    return `há ${diffHours}h`;
  }
  
  if (diffDays === 1) {
    return 'ontem';
  }
  
  if (diffDays < 7) {
    return `há ${diffDays} dias`;
  }
  
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `há ${weeks} sem`;
  }
  
  // More than a month, show formatted date
  return formatDate(date);
}

/**
 * Converts a BRL string (pt-BR or EN format) to centavos (integer).
 * Handles: "1.234,56", "1234.56", "1234", "0", ""
 * NO floating-point arithmetic — pure integer manipulation.
 */
export function toCents(value: string): number {
  if (!value || value.trim() === '') return 0;
  const trimmed = value.trim();
  const isNegative = trimmed.startsWith('-');
  // Remove everything except digits, comma, and dot
  const cleaned = trimmed.replace(/[^\d,.]/g, '');
  if (!cleaned) return 0;

  // Detect format: if last separator is comma, it's pt-BR decimal
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let integerPart: string;
  let decimalPart: string;

  if (lastComma > lastDot) {
    const afterComma = cleaned.substring(lastComma + 1);
    if (afterComma.length === 3 && lastDot === -1) {
      // "1,234" — comma is a thousands separator, no decimals
      integerPart = cleaned.replace(/,/g, '');
      decimalPart = '';
    } else {
      // pt-BR: "1.234,56" or "1234,5"
      integerPart = cleaned.substring(0, lastComma).replace(/\./g, '');
      decimalPart = afterComma;
    }
  } else if (lastDot > lastComma) {
    const afterDot = cleaned.substring(lastDot + 1);
    if (afterDot.length === 3 && lastComma === -1) {
      // "1.234" — dot is a thousands separator, no decimals
      integerPart = cleaned.replace(/\./g, '');
      decimalPart = '';
    } else {
      // EN: "1,234.56" or "1234.5"
      integerPart = cleaned.substring(0, lastDot).replace(/,/g, '');
      decimalPart = afterDot;
    }
  } else {
    // No decimal separator
    integerPart = cleaned.replace(/[.,]/g, '');
    decimalPart = '';
  }

  const intVal = parseInt(integerPart || '0', 10);
  const decVal = parseInt((decimalPart + '00').substring(0, 2), 10);
  const result = intVal * 100 + decVal;
  return isNegative ? -result : result;
}

/**
 * Converts centavos (integer) to BRL display string.
 * 123456 → "1.234,56"
 * -123456 → "-1.234,56"
 */
export function fromCents(cents: number): string {
  const roundedCents = Math.round(cents);
  const isNegative = roundedCents < 0;
  const value = Math.abs(roundedCents);
  const intPart = Math.floor(value / 100);
  const decPart = value % 100;
  const formattedInt = intPart.toLocaleString('pt-BR');
  const formatted = `${formattedInt},${decPart.toString().padStart(2, '0')}`;
  return isNegative ? `-${formatted}` : formatted;
}

/**
 * Format hours into a human-readable time string for pipeline stage durations.
 *
 * null → '—', < 1 → '< 1h', < 24 → '{X}h',
 * < 168 (7d) → '{X}d {Y}h', >= 168 → '{X}sem'
 *
 * @param hours - Duration in hours (or null for no data)
 * @returns Formatted time string
 */
export function formatTimeInStage(hours: number | null): string {
  if (hours === null || hours === undefined) return '—';
  if (hours < 1) return '< 1h';

  const totalHours = Math.floor(hours);

  if (totalHours < 24) return `${totalHours}h`;
  if (totalHours < 168) {
    const days = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  const weeks = Math.max(1, Math.floor(totalHours / 168));
  return `${weeks}sem`;
}